import { ExternalDataSourceType } from '@helios/common-api'

declare global {
  interface Window {
    copilot: CopilotWindowApi
  }
}

/**
 * This is the API that copilot places on the window object.
 * Applications interact with copilot via window.copilot.XXX()
 */
export interface CopilotWindowApi {
  open: () => void
  close: () => void

  //user session
  startSession: (sessionConfig: CopilotSessionConfig) => void
  setSystemPrompt: (systemPromptConfig: SystemPromptConfig | undefined) => void

  getApplicationChatResponse: (
    prompt: string,
    chatOptions?: ChatOptions,
  ) => Promise<string>

  getTemplatesByTagName: (tag: string) => Promise<PromptTemplate[]>

  getPresetTemplateByName: (name: string) => Promise<PromptTemplate | undefined>

  getResponseWithoutSession: (
    prompt: string,
    sessionConfig?: CopilotSessionConfig,
    systemPromptConfig?: SystemPromptConfig,
  ) => Promise<string>

  // UI Elements
  getIconUrl(): string

  startLoading: () => void

  stopLoading: () => void

  addAssistantMessage: (assistantMessage: AssistantMessage) => void

  setActionHandler: (actionHandler: ActionHandler) => void

  setDigitalSpecialist: (dsUsername: string, appName?: string) => void
}

export interface SystemPromptConfig {
  promptTemplateId?: string
  promptTemplateName?: string
  variables?: Record<string, string>
}

export interface CopilotSessionConfig {
  modelInfo: ModelInfo
  chatOptions: ChatOptions
}

export const DEFAULT_CONFIGURATION: CopilotSessionConfig = {
  modelInfo: {
    model: 'gpt-35-turbo-16k',
    deployment: 'gpt-35-turbo-16k',
    provider: 'azure-openai',
    temperature: 0.9,
  },
  chatOptions: {
    tools: [],
  },
}

export interface ModelInfo {
  temperature?: number

  //V1
  deploymentName?: string
  modelName?: string

  //V2
  provider?: string
  deployment?: string
  model?: string
}

export interface ChatOptions {
  dsUsername?: string
  tools?: string[] | Tool[]
  cwan_system_prompts?: boolean
  file?: string
}

export interface Tool {
  name: string
  exclusiveSelection?: boolean
  isDisabled?: boolean
}

export interface PromptTemplate {
  id: string
  name: string
  prompt: string
  showInMenu: boolean
  type: 'preset' | 'custom'
  role: 'system' | 'user'
  default: boolean
  description?: string
  tags: string[]
  createdAt: Date
}

export interface AssistantMessage {
  content: string
  suggestedActions?: AvailableAction[]
}
export interface AvailableAction {
  type: string
  title: string
  params: Record<string, string | string[] | number[] | unknown>
}
export type ActionHandler = (action: AvailableAction) => void

export interface CashResolveRequest {
  securityId: number
  accountId: number
  source: ExternalDataSourceType
  difference: number
  excludeCashTransactions: boolean
  excludeMMFTransactions: boolean
  excludeExternalTransactions: boolean
  excludeUnlinkedTransactions: boolean
  excludeIgnoredTransactions: boolean
}

export interface CashBreakInfo {
  breakId: string
  securityId: number
  assetId: string
  accountId: number
  source: ExternalDataSourceType
  difference: number
  clientName?: string
  clientId?: string
}

export interface CashBreakSuggestion {
  cwTranToClear: CwTransactionSuggestion[]
  cwTranToUnclear: CwTransactionSuggestion[]
  extTranToClear: ExtTransactionSuggestion[]
}

export interface CwTransactionSuggestion {
  tranId: string
  accountId: number
  amount: number
  postDate?: string
  secIdentifier?: string
  currencyCode?: string
}

export interface ExtTransactionSuggestion {
  extTranId: number
  accountId: number
  amount: number
  postDate?: string
  secIdentifier?: string
  currencyCode?: string
}

export class CommandCreationResponse {
  commandId?: number
  command?: never
}
